import React, { useContext, useEffect, useState } from 'react';

import { Card, Row, Spinner } from 'react-bootstrap';
import { Coupon } from '../Coupon';

import { PlanContext } from '../Context/PlanContext';
import { ContentContext } from '../Context/ContentContext';
import { CheckoutPageContext } from '../Context/CheckoutPageContext';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { CouponContext } from '../Context/CouponContext';
import { PlanButton } from '../PlanButton';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { isUrl } from '../../../../utils/validators/url';
import { BillingType } from '../constants/billingType';

const CYCLE_LABEL: Record<number, string> = {
	1: 'por semana',
	2: 'por quinzena',
	3: 'por mês',
	4: 'por bimestre',
	5: 'por trimestre',
	6: 'por semestre',
	7: 'por ano'
};

export const Sidebar = () => {
	const [label, setLabel] = useState<React.ReactNode>(null);

	const { customization } = useContext(ApplicationContext);
	const pageSettings = useContext(CheckoutPageContext);

	const [currentMinusBg, setCurrentMinusBg] = useState<string>('unset');
	const [currentPlusBg, setCurrentPlusBg] = useState<string>('unset');

	const [currentMinusColor, setCurrentMinusColor] = useState<string>(
		customization?.primaryColor || '#fff'
	);
	const [currentPlusColor, setCurrentPlusColor] = useState<string>(
		customization?.primaryColor || '#fff'
	);

	useEffect(() => {
		setCurrentPlusColor(customization?.primaryColor || '#fff');
		setCurrentMinusColor(customization?.primaryColor || '#fff');
	}, [customization]);

	const { orderBumps } = useContext(OrderBumpContext);
	const bumpsTotal = orderBumps.reduce(
		(sum, bump) => sum + (bump.content.pricing?.price || 0),
		0
	);

	const { plan, plans } = useContext(PlanContext);

	const {
		content,
		isLoading,
		currentPrice,
		setCurrentPrice,
		quantity,
		setQuantity,
		billingType
	} = useContext(ContentContext);
	const { coupon } = useContext(CouponContext);

	const image = content?.images.find((image) => {
		return image.type === 1;
	});

	useEffect(() => {
		const installments = content?.pricing.pricingSchema?.installmentsSchema;
		if (!installments || installments.length === 0) return;

		const firstInstallment = installments.find(
			(schema) => schema.count === 1
		);
		const secondInstallment = installments.find(
			(schema) => schema.count > 1
		);

		setLabel(
			<>
				1x de{' '}
				{formatCurrency(
					(firstInstallment?.price || 0) - (coupon?.flatDiscount || 0)
				)}{' '}
				+ <br />
				{installments.length - 1}x de{' '}
				{formatCurrency(secondInstallment?.price || 0)}
			</>
		);
	}, [content, coupon]);

	const handleIncreaseQuantity = () => {
		setQuantity((prev) => prev + 1);
		setCurrentPrice((prev) => prev + (content?.pricing.price || 0));
	};

	const handleDecreaseQuantity = () => {
		if (quantity === 1) return;

		setQuantity((prev) => prev - 1);
		setCurrentPrice((prev) => prev - (content?.pricing.price || 0));
	};

	useEffect(() => {
		const discount = coupon?.flatDiscount || 0;
		const availableCoupons = (coupon?.amount || 0) - (coupon?.used || 0);

		const priceWithoutDiscount = (content?.pricing.price || 0) * quantity;

		availableCoupons >= quantity
			? setCurrentPrice(priceWithoutDiscount - discount * quantity)
			: setCurrentPrice(
					priceWithoutDiscount - discount * availableCoupons
			  );
	}, [quantity, coupon]);

	const [hasDiscount, setHasDiscount] = useState(false);
	useEffect(() => {
		if (coupon) return setHasDiscount(true);

		if (
			billingType === BillingType.CreditCard &&
			content?.pricing.flatCreditCardDiscount
		)
			return setHasDiscount(true);

		if (billingType === BillingType.Pix && content?.pricing.flatPixDiscount)
			return setHasDiscount(true);

		if (
			billingType === BillingType.Boleto &&
			content?.pricing.flatTicketDiscount
		)
			return setHasDiscount(true);

		return setHasDiscount(false);
	}, [coupon, billingType]);

	return (
		<div className="p-6 bg-white rounded-xl sidebar-grid-item">
			<div className="h-100 d-flex flex-column px-5">
				<Card.Header className="pt-md-6 py-0 pb-4 d-flex align-items-center justify-content-between bg-transparent px-0 border-0">
					<Card.Title className="mb-0">
						{/* Logo do checkout (builder) tem precedência sobre a
						    da plataforma. */}
						{pageSettings.logoUrl ? (
							<img
								src={pageSettings.logoUrl}
								alt=""
								className="col-10 px-0"
							/>
						) : (
							customization && (
								<img
									src={
										isUrl(customization?.logo)
											? customization?.logo
											: `${customization?.url}${
													customization?.logo
											  }?d=${Date.now()}`
									}
									alt=""
									className="col-10 px-0"
								/>
							)
						)}
					</Card.Title>
				</Card.Header>
				<Card.Body className="pt-10 pb-10 pb-md-5 px-0">
					{isLoading ? (
						<Row className="justify-content-center">
							<Spinner
								animation="border"
								variant="light"
								style={{
									height: '3rem',
									width: '3rem',
									borderWidth: '.4rem'
								}}
							/>
						</Row>
					) : (
						<>
							{content && (
								<>
									{/* Resumo estilo Stripe: itens
									    discriminados + total. A imagem é
									    configurável (miniatura/grande/
									    oculta) no builder. */}
									{(pageSettings.summaryImage ?? 'thumb') ===
										'full' &&
										image && (
											<div
												style={{
													marginLeft: '-36px',
													marginRight: '-36px'
												}}
											>
												<img
													src={
														isUrl(
															`${image?.path}${image?.name}`
														)
															? `${image?.path}${image?.name}`
															: `${customization?.url}${image?.path}${image?.name}`
													}
													alt=""
													style={{
														maxHeight: '300px',
														objectFit: 'contain'
													}}
													className="w-100 mb-8"
												/>
											</div>
										)}

									<div className="summary-items">
										<div className="summary-item">
											{(pageSettings.summaryImage ??
												'thumb') === 'thumb' &&
												image && (
													<img
														className="summary-thumb"
														src={
															isUrl(
																`${image?.path}${image?.name}`
															)
																? `${image?.path}${image?.name}`
																: `${customization?.url}${image?.path}${image?.name}`
														}
														alt=""
													/>
												)}
											<div className="summary-item-info">
												<span className="summary-item-name">
													{content?.title ||
														plan?.name}
												</span>
												{content.pricing
													?.isBulkPurchaseEnabled && (
													<span className="summary-qty">
														<button
															type="button"
															onClick={
																handleDecreaseQuantity
															}
															className="summary-qty-btn"
														>
															−
														</button>
														<b>{quantity}</b>
														<button
															type="button"
															onClick={
																handleIncreaseQuantity
															}
															className="summary-qty-btn"
														>
															+
														</button>
													</span>
												)}
											</div>
											<span className="summary-item-price">
												{formatCurrency(
													(content?.pricing.price ||
														0) * quantity
												)}
											</span>
										</div>

										{orderBumps.map((bump) => (
											<div
												className="summary-item"
												key={bump.uuid}
											>
												<div className="summary-item-info">
													<span className="summary-item-name">
														{bump.content.title}
													</span>
													<small>
														Oferta adicional
													</small>
												</div>
												<span className="summary-item-price">
													{formatCurrency(
														bump.content.pricing
															?.price || 0
													)}
												</span>
											</div>
										))}

										{hasDiscount &&
											(() => {
												const listTotal =
													((content?.pricing.price ||
														0) +
														bumpsTotal) *
													quantity;
												const currentTotal =
													(currentPrice || 0) +
													bumpsTotal;
												const discount =
													listTotal - currentTotal;
												if (discount <= 0) return null;
												return (
													<div className="summary-item summary-item--discount">
														<div className="summary-item-info">
															<span className="summary-item-name">
																Desconto
															</span>
														</div>
														<span className="summary-item-price">
															−
															{formatCurrency(
																discount
															)}
														</span>
													</div>
												);
											})()}
									</div>

									<div className="summary-total">
										<span>Total</span>
										<b>
											{label
												? label
												: formatCurrency(
														(currentPrice || 0) +
															bumpsTotal
												  )}
										</b>
									</div>

									{/* Parcelamento em destaque (Configurações
										do builder): "ou até 12x de R$ X". */}
									{pageSettings.showInstallments &&
										!label &&
										(() => {
											const schema =
												content?.pricing
													?.pricingSchema;
											const maxInstallment =
												schema?.maxInstallment || 1;
											if (maxInstallment <= 1)
												return null;
											const total =
												(currentPrice || 0) +
													bumpsTotal;
											if (total <= 0) return null;
											const base =
												total / maxInstallment;
											// Mesma taxa progressiva do
											// seletor de parcelas (2,99%/mês).
											let taxes = 0;
											for (
												let i = 1;
												i <= maxInstallment;
												i++
											) {
												taxes += base * i * 0.0299;
											}
											const perInstallment =
												base +
												Math.ceil(taxes * 100) /
													100 /
													maxInstallment;
											return (
												<div className="summary-installments">
													ou até{' '}
													<b>
														{maxInstallment}x de{' '}
														{formatCurrency(
															perInstallment
														)}
													</b>{' '}
													no cartão
												</div>
											);
										})()}

									{content && (
										<div className="d-flex justify-content-start mt-4">
											<Coupon />
										</div>
									)}
								</>
							)}
							{plans && (
								<div>
									<span className="h4 text-gray-800">
										Escolha o seu plano:
									</span>
									{plans.map((plan) => (
										<PlanButton key={plan.id} plan={plan} />
									))}
									{plan && (
										<div className="summary-total mt-6">
											<span>Total</span>
											<b>
												{formatCurrency(plan.price)}{' '}
												<small>
													{CYCLE_LABEL[plan.cycle] ??
														''}
												</small>
											</b>
										</div>
									)}
								</div>
							)}
						</>
					)}
				</Card.Body>
				<div className="d-none d-md-block justify-self-end mb-15 text-center text-muted">
					Powered by <span className="font-weight-bolder">Waid</span>
				</div>
			</div>
		</div>
	);
};
